import { Component, Input, Output, EventEmitter, OnInit, HostListener, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import {
  SpreadsheetBoardData,
  SpreadsheetCell,
  SpreadsheetCellPosition,
  SpreadsheetSelection,
  SpreadsheetCellRange,
  SpreadsheetMergedCell,
  getCellKey,
  getColumnLabel,
  getRowLabel,
  SpreadsheetCellType
} from 'src/app/services/models/spreadsheet-board.model';

@Component({
  selector: 'bhub-spreadsheet-grid',
  templateUrl: './spreadsheet-grid.component.html',
  styleUrls: ['./spreadsheet-grid.component.scss'],
  standalone: false
})
export class SpreadsheetGridComponent implements OnInit, AfterViewInit {
  @ViewChild('spreadsheetTable', { static: true }) spreadsheetTable!: ElementRef<HTMLTableElement>;
  @Input() data: SpreadsheetBoardData = {
    cells: {},
    mergedCells: [],
    rowCount: 20,
    columnCount: 10,
    defaultRowHeight: 28,
    defaultColumnWidth: 100
  };

  @Input() isEditable: boolean = true;
  @Output() selectionChanged = new EventEmitter<SpreadsheetSelection>();
  @Output() cellDoubleClick = new EventEmitter<SpreadsheetCellPosition>();

  selection: SpreadsheetSelection | null = null;
  isSelecting: boolean = false;
  rows: number[] = [];
  columns: number[] = [];
  private columnWidthCache: { [key: number]: number } = {};

  ngOnInit() {
    this.initializeGrid();
  }

  ngOnChanges() {
    this.initializeGrid();
  }

  ngAfterViewInit() {
    // Component is fully initialized, DOM measurements are now available
    // Clear cache to ensure fresh measurements after view init
    setTimeout(() => {
      this.clearColumnWidthCache();
    }, 100);
  }

  private initializeGrid(): void {
    if (!this.data) {
      return;
    }

    this.rows = Array.from({ length: this.data.rowCount }, (_, i) => i);
    this.columns = Array.from({ length: this.data.columnCount }, (_, i) => i);

    // Clear column width cache when grid is re-initialized
    this.clearColumnWidthCache();
  }

  getColumnLabel(index: number): string {
    return getColumnLabel(index);
  }

  getRowLabel(index: number): string {
    return getRowLabel(index);
  }

  getCell(row: number, column: number): SpreadsheetCell | null {
    const key = getCellKey(row, column);
    return this.data.cells[key] || null;
  }

  getCellContent(row: number, column: number): string {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type === SpreadsheetCellType.Empty) {
      return '';
    }

    switch (cell.content.type) {
      case SpreadsheetCellType.StaticText:
        return cell.content.staticText?.text || '';
      case SpreadsheetCellType.Metric:
        return this.formatMetricValue(cell.content.metric);
      case SpreadsheetCellType.Table:
        // Return empty string for table cells as they render their own content
        return '';
      default:
        return '';
    }
  }

  getCellClasses(row: number, column: number): string {
    const classes = ['spreadsheet-cell'];
    const cell = this.getCell(row, column);

    if (cell && cell.content.type !== SpreadsheetCellType.Empty) {
      classes.push('has-content');
      classes.push(cell.content.type);
    }

    // Selection classes
    if (this.selection) {
      const pos = { row, column };

      if (this.selection.type === 'single') {
        if (this.selection.startCell.row === row && this.selection.startCell.column === column) {
          classes.push('selected');
        }
      } else if (this.selection.type === 'range' && this.selection.endCell) {
        const range: SpreadsheetCellRange = {
          startRow: Math.min(this.selection.startCell.row, this.selection.endCell.row),
          endRow: Math.max(this.selection.startCell.row, this.selection.endCell.row),
          startColumn: Math.min(this.selection.startCell.column, this.selection.endCell.column),
          endColumn: Math.max(this.selection.startCell.column, this.selection.endCell.column)
        };

        if (this.isPositionInRange(pos, range)) {
          classes.push('in-selection');

          // Border classes for range selection
          if (row === range.startRow) classes.push('range-top');
          if (row === range.endRow) classes.push('range-bottom');
          if (column === range.startColumn) classes.push('range-left');
          if (column === range.endColumn) classes.push('range-right');
        }
      }
    }

    return classes.join(' ');
  }

  private isPositionInRange(position: SpreadsheetCellPosition, range: SpreadsheetCellRange): boolean {
    return position.row >= range.startRow &&
           position.row <= range.endRow &&
           position.column >= range.startColumn &&
           position.column <= range.endColumn;
  }

  onCellMouseDown(event: MouseEvent, row: number, column: number): void {
    if (!this.isEditable) return;

    event.preventDefault();

    this.isSelecting = true;
    this.selection = {
      type: 'single',
      startCell: { row, column }
    };

    this.selectionChanged.emit(this.selection);
  }

  onCellMouseEnter(event: MouseEvent, row: number, column: number): void {
    if (!this.isEditable || !this.isSelecting || !this.selection) return;

    if (this.selection.startCell.row !== row || this.selection.startCell.column !== column) {
      this.selection = {
        type: 'range',
        startCell: this.selection.startCell,
        endCell: { row, column }
      };

      this.selectionChanged.emit(this.selection);
    }
  }

  onCellDoubleClick(row: number, column: number): void {
    if (!this.isEditable) return;

    this.cellDoubleClick.emit({ row, column });
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isSelecting) {
      this.isSelecting = false;
    }
  }

  onTableMouseEnter(event: MouseEvent): void {
    // Stop drag selection when entering table areas
    if (this.isSelecting) {
      this.isSelecting = false;
      event.stopPropagation();
    }
  }

  getTableDataCellClass(row: number, column: number, colIndex: number): string {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return 'table-data-cell';
    }

    const table = cell.content.table;
    let classes = ['table-data-cell'];

    // Add sticky-left class for first column in vertical tables
    if (table?.headerOrientation === 'vertical' && colIndex === 0) {
      classes.push('sticky-left');
    }

    return classes.join(' ');
  }


  getSelectionInfo(): string {
    if (!this.selection) return '';

    if (this.selection.type === 'single') {
      return `${this.getColumnLabel(this.selection.startCell.column)}${this.getRowLabel(this.selection.startCell.row)}`;
    } else if (this.selection.endCell) {
      const startCol = this.getColumnLabel(this.selection.startCell.column);
      const startRow = this.getRowLabel(this.selection.startCell.row);
      const endCol = this.getColumnLabel(this.selection.endCell.column);
      const endRow = this.getRowLabel(this.selection.endCell.row);
      return `${startCol}${startRow}:${endCol}${endRow}`;
    }

    return '';
  }

  getCellStyle(row: number, column: number): any {
    const cell = this.getCell(row, column);
    if (!cell) {
      return {};
    }

    let styles: any = {};

    if (cell.content.type === SpreadsheetCellType.StaticText) {
      const style = cell.content.staticText?.style;
      if (style) {
        styles = {
          'font-weight': style.fontWeight || 'normal',
          'font-size': style.fontSize || '14px',
          'color': style.color || '#000000',
          'background-color': style.backgroundColor || 'transparent'
        };
      }
    } else if (cell.content.type === SpreadsheetCellType.Metric) {
      const metric = cell.content.metric;
      if (metric?.colorSettings) {
        styles['color'] = this.getMetricColor(metric);
      }
    }

    return styles;
  }

  getCellTextAlign(row: number, column: number): string {
    const cell = this.getCell(row, column);
    if (cell?.content?.type === SpreadsheetCellType.StaticText) {
      return cell.content.staticText?.style?.textAlign || 'left';
    }
    return 'left';
  }

  hasCustomStyle(row: number, column: number): boolean {
    const cell = this.getCell(row, column);
    if (cell?.content?.type === SpreadsheetCellType.StaticText) {
      const style = cell.content.staticText?.style;
      if (style) {
        // Check if any custom styling properties are set (different from defaults)
        const hasCustomFontWeight = style.fontWeight && style.fontWeight !== 'normal';
        const hasCustomFontSize = style.fontSize && style.fontSize !== '14px';
        const hasCustomColor = style.color && style.color !== '#000000';
        const hasCustomBackground = style.backgroundColor &&
          style.backgroundColor !== '#ffffff' &&
          style.backgroundColor !== 'transparent' &&
          style.backgroundColor !== '';
        const hasCustomAlign = style.textAlign && style.textAlign !== 'left';

        return hasCustomFontWeight || hasCustomFontSize || hasCustomColor || hasCustomBackground || hasCustomAlign;
      }
    }
    return false;
  }

  hasCustomBackground(row: number, column: number): boolean {
    const cell = this.getCell(row, column);
    if (cell?.content?.type === SpreadsheetCellType.StaticText) {
      const style = cell.content.staticText?.style;
      if (style && style.backgroundColor) {
        // Check if background color is not default (white or transparent)
        return style.backgroundColor !== '#ffffff' &&
               style.backgroundColor !== 'transparent' &&
               style.backgroundColor !== '' &&
               style.backgroundColor !== 'white';
      }
    }
    return false;
  }

  getTableWrapperStyle(row: number, column: number): any {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return {};
    }

    const table = cell.content.table;
    if (!table) return {};

    // Use the stored selected dimensions from table configuration
    let selectedRows = table.selectedRows;
    let selectedColumns = table.selectedColumns;

    // Fallback to cell span for existing tables without selectedRows/selectedColumns
    if (!selectedRows || !selectedColumns) {
      const cellSpan = this.getCellSpan(row, column);
      selectedRows = selectedRows || cellSpan.rowSpan;
      selectedColumns = selectedColumns || cellSpan.colSpan;
    }

    // Optimize scrolling based on header orientation
    if (table.headerOrientation === 'horizontal') {
      // Horizontal headers: width is fixed by table structure, only need vertical scroll
      const dimensions = this.measureCellDimensions(selectedRows, selectedColumns);
      return {
        'width': '100%',
        'height': `${dimensions.height}px`,
        'max-width': '100%',
        'max-height': `${dimensions.height}px`,
        'min-width': '100%',
        'min-height': `${dimensions.height}px`,
        'overflow-x': 'hidden', // No horizontal scroll needed
        'overflow-y': 'auto'    // Only vertical scroll
      };
    } else {
      // Vertical headers: height is fixed by table structure, only need horizontal scroll
      const dimensions = this.measureCellDimensions(selectedRows, selectedColumns);
      return {
        'width': `${dimensions.width}px`,
        'height': '100%',
        'max-width': `${dimensions.width}px`,
        'max-height': '100%',
        'min-width': `${dimensions.width}px`,
        'min-height': '100%',
        'overflow-x': 'auto',   // Only horizontal scroll
        'overflow-y': 'hidden' // No vertical scroll needed
      };
    }
  }

  private measureCellDimensions(rows: number, columns: number): { width: number, height: number } {
    if (!this.spreadsheetTable?.nativeElement) {
      // Fallback to fixed dimensions if DOM not available
      return { width: 100 * columns, height: 28 * rows };
    }

    const table = this.spreadsheetTable.nativeElement;

    // Get actual column widths by measuring header cells (excluding corner cell)
    const headerRow = table.querySelector('tr:first-child');
    const headerCells = headerRow?.querySelectorAll('th.column-header');

    let totalWidth = 0;
    if (headerCells && headerCells.length >= columns) {
      for (let i = 0; i < columns; i++) {
        const cell = headerCells[i] as HTMLElement;
        totalWidth += cell.offsetWidth;
      }
    } else {
      // Fallback if we can't measure
      totalWidth = 100 * columns;
    }

    // Get actual row heights by measuring data rows
    const dataRows = table.querySelectorAll('tr.spreadsheet-row');
    let totalHeight = 0;

    if (dataRows && dataRows.length >= rows) {
      for (let i = 0; i < rows; i++) {
        const row = dataRows[i] as HTMLElement;
        totalHeight += row.offsetHeight;
      }
    } else {
      // Fallback if we can't measure
      totalHeight = 28 * rows;
    }

    return { width: totalWidth, height: totalHeight };
  }

  private formatMetricValue(metric: any): string {
    if (!metric) return 'Metric';

    // Use calculated value if available, otherwise use mock value
    let value: number;
    if (metric.calculatedValue !== undefined && metric.calculatedValue !== null) {
      value = parseFloat(metric.calculatedValue);
    } else {
      // Fallback to mock value for demo/preview purposes
      value = 1234.56;
    }

    let formattedValue = value.toLocaleString('en-US');

    // Apply custom unit if specified
    if (metric.unit) {
      if (metric.unit === '$') {
        formattedValue = metric.unit + formattedValue;
      } else {
        formattedValue = formattedValue + metric.unit;
      }
    }

    return formattedValue;
  }

  private getMetricColor(metric: any): string {
    return metric.colorSettings?.defaultColor || '#000000';
  }

  getMergedCell(row: number, column: number): any {
    if (!this.data.mergedCells) return null;

    for (const merge of this.data.mergedCells) {
      if (row >= merge.startRow && row <= merge.endRow &&
          column >= merge.startColumn && column <= merge.endColumn) {
        return merge;
      }
    }
    return null;
  }

  isCellHidden(row: number, column: number): boolean {
    const merge = this.getMergedCell(row, column);
    if (!merge) return false;

    // Only the top-left cell of a merge is visible
    return !(row === merge.startRow && column === merge.startColumn);
  }

  isMergedMainCell(row: number, column: number): boolean {
    const merge = this.getMergedCell(row, column);
    return merge !== null && row === merge.startRow && column === merge.startColumn;
  }

  getCellSpan(row: number, column: number): { rowSpan: number; colSpan: number } {
    const merge = this.getMergedCell(row, column);
    if (!merge || row !== merge.startRow || column !== merge.startColumn) {
      return { rowSpan: 1, colSpan: 1 };
    }

    return {
      rowSpan: merge.endRow - merge.startRow + 1,
      colSpan: merge.endColumn - merge.startColumn + 1
    };
  }

  getMergedCellStyle(row: number, column: number): any {
    const merge = this.getMergedCell(row, column);

    if (!merge || (row !== merge.startRow || column !== merge.startColumn)) {
      return {};
    }

    // Calculate the width and height for the merged cell
    const colSpan = merge.endColumn - merge.startColumn + 1;
    const rowSpan = merge.endRow - merge.startRow + 1;

    // Base dimensions
    const cellWidth = 100; // matching the CSS width
    const cellHeight = 28; // matching the CSS height
    const borderWidth = 1;

    // Calculate total dimensions
    const totalWidth = (cellWidth * colSpan) + (borderWidth * (colSpan - 1));
    const totalHeight = (cellHeight * rowSpan) + (borderWidth * (rowSpan - 1));

    return {
      'width': `${totalWidth}px`,
      'min-width': `${totalWidth}px`,
      'max-width': `${totalWidth}px`,
      'height': `${totalHeight}px`,
      'min-height': `${totalHeight}px`,
      'position': 'relative',
      'z-index': '10'
    };
  }

  getTableHeaders(row: number, column: number): string[] {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return [];
    }

    const table = cell.content.table;
    if (!table) return [];

    if (table.headerOrientation === 'horizontal') {
      // Horizontal: dimension column + all metric columns
      const headers = [table.groupByFieldCustomName || table.groupByFieldName || 'Dimension'];

      // Add all metric headers (no limiting)
      for (let i = 0; i < table.headers.length; i++) {
        const header = table.headers[i];
        headers.push(header.customName || header.displayName || header.metricName || 'Metric');
      }

      return headers;
    } else {
      // Vertical: no separate header row needed - headers are part of data rows
      return [];
    }
  }

  getTableDataRows(row: number, column: number): string[][] {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return [];
    }

    const table = cell.content.table;
    if (!table) return [];

    // Use real data if available, otherwise fall back to mock data
    const calculatedData = table.calculatedData;
    const mockValues = ['123.45', '456.78', '789.01', '234.56', '567.89'];

    if (table.headerOrientation === 'horizontal') {
      // Horizontal layout: show all data rows
      const dataRows: string[][] = [];

      if (calculatedData && calculatedData.length > 0) {
        // Use all real data (no limiting)
        calculatedData.forEach(dataRow => {
          const row: string[] = [];

          // Add dimension value using the groupByField
          const fieldValue = dataRow[table.groupByField];
          row.push(fieldValue !== undefined ? String(fieldValue) : 'Unknown');

          // Add metric values for all headers (no limiting)
          for (let j = 0; j < table.headers.length; j++) {
            const header = table.headers[j];
            const value = dataRow[header.metricName];
            row.push(value !== undefined ? String(value) : 'N/A');
          }

          dataRows.push(row);
        });
      } else {
        // Fall back to expanded mock data
        for (let i = 0; i < 15; i++) { // Show more mock rows
          const row = [`Group ${i + 1}`];

          for (let j = 0; j < table.headers.length; j++) {
            row.push(mockValues[(i + j) % mockValues.length]);
          }

          dataRows.push(row);
        }
      }

      return dataRows;
    } else {
      // Vertical layout: show all data and metrics
      const dataRows: string[][] = [];

      if (calculatedData && calculatedData.length > 0) {
        // Use all real data
        // First row: all dimension values
        const dimensionRow = [table.groupByFieldCustomName || table.groupByFieldName || 'Dimension'];

        calculatedData.forEach(dataRow => {
          const fieldValue = dataRow[table.groupByField];
          dimensionRow.push(fieldValue !== undefined ? String(fieldValue) : 'Unknown');
        });
        dataRows.push(dimensionRow);

        // All metric rows (no limiting)
        for (let i = 0; i < table.headers.length; i++) {
          const header = table.headers[i];
          const row = [header.customName || header.displayName || header.metricName || 'Metric'];

          calculatedData.forEach(dataRow => {
            const value = dataRow[header.metricName];
            row.push(value !== undefined ? String(value) : 'N/A');
          });

          dataRows.push(row);
        }
      } else {
        // Fall back to expanded mock data
        const dimensionRow = [table.groupByFieldCustomName || table.groupByFieldName || 'Dimension'];
        for (let i = 0; i < 8; i++) { // Show more mock columns
          dimensionRow.push(`Group ${i + 1}`);
        }
        dataRows.push(dimensionRow);

        // Show all metric headers (no limiting)
        for (let i = 0; i < table.headers.length; i++) {
          const header = table.headers[i];
          const row = [header.customName || header.displayName || header.metricName || 'Metric'];

          for (let j = 0; j < 8; j++) {
            row.push(mockValues[(i + j) % mockValues.length]);
          }
          dataRows.push(row);
        }
      }

      return dataRows;
    }
  }

  getTableHeaderStyle(row: number, column: number, headerIndex: number): any {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return {};
    }

    const table = cell.content.table;
    const colorConfig = table?.colorConfig;

    let styles: any = {};

    // Set colors
    if (colorConfig) {
      styles['background-color'] = colorConfig.headerBackgroundColor;
      styles['color'] = colorConfig.headerTextColor;
    } else {
      styles['background-color'] = '#e0e0e0';
      styles['color'] = '#000000';
    }

    // For vertical tables, only match spreadsheet width for the first column (headers column)
    if (table?.headerOrientation === 'vertical' && headerIndex === 0) {
      const columnWidth = this.getSpreadsheetColumnWidth(column);
      if (columnWidth) {
        styles['width'] = `${columnWidth}px !important`;
        styles['min-width'] = `${columnWidth}px !important`;
        styles['max-width'] = `${columnWidth}px !important`;
      }
    }

    return styles;
  }

  getTableDataCellStyle(row: number, column: number, dataRowIndex: number, dataCellIndex: number): any {
    const cell = this.getCell(row, column);
    if (!cell || cell.content.type !== SpreadsheetCellType.Table) {
      return {};
    }

    const table = cell.content.table;
    const colorConfig = table?.colorConfig;

    let styles: any = {};

    // Check if this is a grouping cell (first column/row)
    const isGroupingCell = (table.headerOrientation === 'horizontal' && dataCellIndex === 0) ||
                          (table.headerOrientation === 'vertical' && dataRowIndex === 0);

    // Set colors
    if (!colorConfig) {
      // Default colors
      if (isGroupingCell) {
        styles['background-color'] = '#f5f5f5';
        styles['color'] = '#000000';
      } else {
        const isAlternating = (table.headerOrientation === 'horizontal' ? dataRowIndex : dataCellIndex) % 2 === 1;
        styles['background-color'] = isAlternating ? '#f0f8ff' : '#ffffff';
        styles['color'] = '#000000';
      }
    } else {
      // Custom colors
      if (isGroupingCell) {
        styles['background-color'] = colorConfig.groupingBackgroundColor;
        styles['color'] = colorConfig.groupingTextColor;
      } else {
        const isAlternating = (table.headerOrientation === 'horizontal' ? dataRowIndex : dataCellIndex) % 2 === 1;
        styles['background-color'] = isAlternating ? colorConfig.dataBackgroundColor2 : colorConfig.dataBackgroundColor1;
        styles['color'] = colorConfig.dataTextColor;
      }
    }

    // For vertical tables, only match spreadsheet width for the first column (headers column)
    if (table.headerOrientation === 'vertical' && dataCellIndex === 0) {
      const columnWidth = this.getSpreadsheetColumnWidth(column);
      if (columnWidth) {
        styles['width'] = `${columnWidth}px !important`;
        styles['min-width'] = `${columnWidth}px !important`;
        styles['max-width'] = `${columnWidth}px !important`;
      }
    }

    return styles;
  }

  private getSpreadsheetColumnWidth(columnIndex: number): number | null {
    // Return cached value if available
    if (this.columnWidthCache[columnIndex] !== undefined) {
      return this.columnWidthCache[columnIndex];
    }

    if (!this.spreadsheetTable?.nativeElement) {
      // Cache null result to prevent repeated DOM queries
      this.columnWidthCache[columnIndex] = null;
      return null;
    }

    try {
      const table = this.spreadsheetTable.nativeElement;
      const headerRow = table.querySelector('tr:first-child');
      const headerCells = headerRow?.querySelectorAll('th.column-header');

      if (headerCells && columnIndex < headerCells.length) {
        const cell = headerCells[columnIndex] as HTMLElement;
        const width = cell.offsetWidth;
        // Cache the measured width
        this.columnWidthCache[columnIndex] = width;
        return width;
      }
    } catch (error) {
      console.warn('Error measuring column width:', error);
    }

    // Cache null result to prevent repeated failed attempts
    this.columnWidthCache[columnIndex] = null;
    return null;
  }

  private clearColumnWidthCache(): void {
    this.columnWidthCache = {};
  }
}
